import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, User, Tag } from 'lucide-react';
import GradientText from '../components/GradientText';
import { PremiumBackground } from '../components/EnhancedBackground';
import { FloatingOrbs, AnimatedGrid } from '../components/PremiumEffects';
import { blogAPI } from '../services/api';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) {
        setError('Invalid link');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await blogAPI.getById(id);
        if (!cancelled) setPost(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this article.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white flex items-center justify-center">
        <PremiumBackground />
        <p className="text-blue-200">Loading article…</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white flex flex-col items-center justify-center px-4">
        <PremiumBackground />
        <p className="text-red-300 mb-6 text-center max-w-md">{error || 'Article not found.'}</p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium hover:opacity-95"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to blog
        </Link>
      </div>
    );
  }

  const isHtmlContent = typeof post.content === 'string' && /<\/?[a-z][\s\S]*>/i.test(post.content);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
      <PremiumBackground />
      <FloatingOrbs />
      <AnimatedGrid />

      <article className="relative pt-24 pb-16 px-4">
        <div className="container-custom max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-blue-300 hover:text-white mb-8 text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              All articles
            </Link>

            {post.image && (
              <div className="rounded-2xl overflow-hidden border border-blue-400/20 mb-8 aspect-[21/9] md:aspect-[2/1]">
                <img src={post.image} alt="" className="w-full h-full object-cover" />
              </div>
            )}

            <span className="inline-block px-3 py-1 bg-blue-600/40 text-blue-100 text-sm font-medium rounded-full mb-4">
              {post.category}
            </span>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              <GradientText className="text-3xl md:text-4xl lg:text-5xl">{post.title}</GradientText>
            </h1>

            <div className="flex flex-wrap gap-4 text-sm text-blue-200 mb-8">
              <span className="inline-flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {post.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-10">
                {post.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-900/60 text-blue-100 text-xs rounded border border-blue-400/20"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="prose prose-invert prose-lg max-w-none border-t border-blue-400/20 pt-10">
              {isHtmlContent ? (
                <div
                  className="text-blue-50/95 leading-relaxed blog-post-content"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              ) : (
                <div className="text-blue-50/95 leading-relaxed whitespace-pre-wrap">{post.content}</div>
              )}
            </div>
          </motion.div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
